import * as React from 'react'
import { shallow } from 'zustand/shallow'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import InputLabel from '@mui/material/InputLabel'
import TextField from '@mui/material/TextField'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import Typography from '@mui/material/Typography'

import * as Yup from 'yup'
import { useFormik } from 'formik'
import { useAuth0 } from '@auth0/auth0-react'

import EmpresasCheckboxList from './EmpresasCheckboxList'

import { sendInviteToUser } from '../../../services/UsersService'

import { useDrawerStore } from '../../../store/drawerStore'
import { useEmpresaStore } from '../../../store/empresaStore'
import { CircularProgress, Fade } from '@mui/material'

type NewUserModalType = {
  openModal: boolean
  handleRefreshTable: React.Dispatch<React.SetStateAction<boolean>>
  changeOpenModal: React.Dispatch<React.SetStateAction<boolean>>
  changeOpenInfoModal: React.Dispatch<React.SetStateAction<boolean>>
  setErrorMessage?: React.Dispatch<React.SetStateAction<string>>
}

const NewUserModal: React.FC<NewUserModalType> = ({
  changeOpenModal,
  handleRefreshTable,
  openModal,
  changeOpenInfoModal,
  setErrorMessage,
}) => {
  const { t } = useTranslation('newUserModal')
  const [disabledOnSubmit, setDisabledOnSubmit] = React.useState<boolean>(false)
  const [showPassword, setShowPassword] = React.useState<boolean>(false)
  const [showConfirmPassword, setShowConfirmPassword] = React.useState<boolean>(false)
  const { getAccessTokenSilently } = useAuth0()

  const selectedEmpresa = useEmpresaStore(state => state.selectedEmpresa, shallow)

  const changeLink = useDrawerStore(state => state.changeLink, shallow)

  const formik = useFormik({
    initialValues: {
      name: '',
      last_name: '',
      email: '',
      companies: [],
      provisional_password: '',
      provisional_password_confirm: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().max(55).required(t('validation.nameRequired')),
      last_name: Yup.string().max(55).required(t('validation.lastNameRequired')),
      email: Yup.string().max(255).required(t('validation.emailRequired')),
      companies: Yup.array()
        .min(1, t('validation.companiesMinRequired'))
        .required(t('validation.companiesMinRequired')),
      provisional_password: Yup.string()
        .min(10, t('validation.passwordMinLength'))
        .matches(/[a-z]/, t('validation.passwordLower'))
        .matches(/[A-Z]/, t('validation.passwordUpper'))
        .matches(/[0-9]/, t('validation.passwordNumber'))
        .matches(/[!@#$%^&*()\-_=+[\]{};:'",.<>/?\\|`~]/, t('validation.passwordSpecial'))
        .test(
          'no-triple-repeat',
          t('validation.passwordNoTripleRepeat'),
          value => !value || !(/(.)\1{2,}/.test(value)),
        )
        .required(t('validation.passwordRequired')),
      provisional_password_confirm: Yup.string()
        .oneOf([Yup.ref('provisional_password')], t('validation.passwordsMustMatch'))
        .required(t('validation.confirmPasswordRequired')),
    }),
    onSubmit: async data => {
      setDisabledOnSubmit(true)
      const token = await getAccessTokenSilently()

      sendInviteToUser(token, data)
        .then(result => {
          formik.resetForm()
          if (result.status === 201) {
            setDisabledOnSubmit(false)
            handleRefreshTable(true)
            changeOpenModal(false)
          }
        })
        .catch(e => {
          console.log(e, ' user error by invitaation')
          changeOpenModal(false)
          changeOpenInfoModal(true)
          setErrorMessage && setErrorMessage(t('userAlreadyExistsError'))
          setDisabledOnSubmit(false)
        })
    },
  })

  const pwdChecks = React.useMemo(() => {
    const v = formik.values.provisional_password
    return {
      minLength: v.length >= 10,
      hasLower: /[a-z]/.test(v),
      hasUpper: /[A-Z]/.test(v),
      hasNumber: /[0-9]/.test(v),
      hasSpecial: /[!@#$%^&*()\-_=+[\]{};:'",.<>/?\\|`~]/.test(v),
      noTripleRepeat: v.length === 0 || !(/(.)\1{2,}/.test(v)),
    }
  }, [formik.values.provisional_password])

  const handleClose = () => {
    changeOpenModal(false)
  }

  React.useLayoutEffect(() => {
    changeLink === 2 &&
      formik.setFieldValue('companies', [{ company_id: selectedEmpresa?.id, role: 1 }])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeLink, selectedEmpresa?.id])

  return (
    <div>
      <Dialog
        open={openModal}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: '40%',
          },
        }}
      >
        {disabledOnSubmit ? (
          <Box
            sx={{
              display: 'flex',
              height: '348px',
              placeContent: 'center',
              placeItems: 'center',
            }}
          >
            <Fade in={disabledOnSubmit}>
              <div>
                <CircularProgress />
              </div>
            </Fade>
          </Box>
        ) : (
          <>
            <DialogTitle>{t('title')}</DialogTitle>
            <DialogContent>
              <Grid container borderRadius="6px">
                <Grid bgcolor="white" item xs borderRadius="6px">
                  <Box
                    sx={{
                      borderRadius: '6px',
                      display: 'flex',
                      backgroundColor: 'white',
                      height: '100%',
                      width: '100%',
                    }}
                  >
                    <Box sx={{ flexGrow: 1 }}>
                      <Box
                        component="form"
                        id="crear-usuario-form"
                        autoComplete="off"
                        borderRadius="6px"
                        sx={{ backgroundColor: 'white', height: '100%' }}
                        onSubmit={formik.handleSubmit}
                      >
                        <Grid container>
                          <Grid bgcolor="white" item xs>
                            <Grid container>
                              <Grid item xs sx={{ py: '10px' }}>
                                <Box
                                  display="flex"
                                  justifyContent="flex-start"
                                  marginBottom="4px"
                                >
                                  <InputLabel
                                    sx={{ color: 'black' }}
                                    htmlFor="nombre-input"
                                  >
                                    {t('fields.name')}
                                  </InputLabel>
                                </Box>
                                <TextField
                                  id="nombre-input"
                                  error={Boolean(
                                    formik.touched.name && formik.errors.name,
                                  )}
                                  fullWidth
                                  helperText={formik.touched.name && formik.errors.name}
                                  name="name"
                                  onBlur={formik.handleBlur}
                                  onChange={formik.handleChange}
                                  type="text"
                                  value={formik.values.name}
                                  variant="filled"
                                  placeholder={t('fields.namePlaceholder')}
                                  InputProps={{
                                    disableUnderline: true,
                                    sx: {
                                      borderRadius: '6px',
                                      '.MuiInputBase-input': {
                                        paddingBottom: '16px',
                                        paddingTop: '16px',
                                      },
                                    },
                                  }}
                                />
                              </Grid>
                              <Grid item xs sx={{ py: '10px', pl: '18px' }}>
                                <Box
                                  display="flex"
                                  justifyContent="flex-start"
                                  marginBottom="4px"
                                >
                                  <InputLabel
                                    sx={{ color: 'black' }}
                                    htmlFor="apellido-input"
                                  >
                                    {t('fields.lastName')}
                                  </InputLabel>
                                </Box>
                                <TextField
                                  id="apellido-input"
                                  error={Boolean(
                                    formik.touched.last_name && formik.errors.last_name,
                                  )}
                                  fullWidth
                                  helperText={
                                    formik.touched.last_name && formik.errors.last_name
                                  }
                                  name="last_name"
                                  onBlur={formik.handleBlur}
                                  onChange={formik.handleChange}
                                  type="text"
                                  value={formik.values.last_name}
                                  variant="filled"
                                  placeholder={t('fields.lastNamePlaceholder')}
                                  InputProps={{
                                    disableUnderline: true,
                                    sx: {
                                      borderRadius: '6px',
                                      '.MuiInputBase-input': {
                                        paddingBottom: '16px',
                                        paddingTop: '16px',
                                      },
                                    },
                                  }}
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid container>
                          <Grid bgcolor="white" item xs>
                            <Grid container>
                              <Grid item xs={6} sx={{ py: '10px', pr: '8px' }}>
                                <Box
                                  display="flex"
                                  justifyContent="flex-start"
                                  marginBottom="4px"
                                >
                                  <InputLabel
                                    sx={{ color: 'black' }}
                                    htmlFor="email-input"
                                  >
                                    {t('fields.email')}
                                  </InputLabel>
                                </Box>
                                <TextField
                                  id="email-input"
                                  error={Boolean(
                                    formik.touched.email && formik.errors.email,
                                  )}
                                  fullWidth
                                  helperText={formik.touched.email && formik.errors.email}
                                  name="email"
                                  onBlur={formik.handleBlur}
                                  onChange={formik.handleChange}
                                  type="email"
                                  autoComplete="off"
                                  value={formik.values.email}
                                  variant="filled"
                                  placeholder={t('fields.emailPlaceholder')}
                                  InputProps={{
                                    disableUnderline: true,
                                    sx: {
                                      borderRadius: '6px',
                                      '.MuiInputBase-input': {
                                        paddingBottom: '16px',
                                        paddingTop: '16px',
                                      },
                                    },
                                  }}
                                />
                              </Grid>
                              <Grid item xs={6} sx={{ py: '10px', pl: '8px' }}>
                                <Box
                                  display="flex"
                                  justifyContent="flex-start"
                                  marginBottom="4px"
                                >
                                  <InputLabel
                                    sx={{ color: 'black' }}
                                    htmlFor="provisional-password-input"
                                  >
                                    {t('fields.provisionalPassword')}
                                  </InputLabel>
                                </Box>
                                <TextField
                                  id="provisional-password-input"
                                  error={Boolean(
                                    formik.touched.provisional_password &&
                                      formik.errors.provisional_password,
                                  )}
                                  fullWidth
                                  name="provisional_password"
                                  onBlur={formik.handleBlur}
                                  onChange={formik.handleChange}
                                  autoComplete="new-password"
                                  type={showPassword ? 'text' : 'password'}
                                  value={formik.values.provisional_password}
                                  variant="filled"
                                  placeholder={t('fields.provisionalPasswordPlaceholder')}
                                  InputProps={{
                                    disableUnderline: true,
                                    sx: {
                                      borderRadius: '6px',
                                      '.MuiInputBase-input': {
                                        paddingBottom: '16px',
                                        paddingTop: '16px',
                                      },
                                    },
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          onClick={() => setShowPassword(v => !v)}
                                          edge="end"
                                        >
                                          {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                                {formik.values.provisional_password.length > 0 && (
                                  <Box sx={{ mt: 0.75, px: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                    {[
                                      { met: pwdChecks.minLength, label: t('passwordChecks.minLength') },
                                      { met: pwdChecks.hasLower, label: t('passwordChecks.hasLower') },
                                      { met: pwdChecks.hasUpper, label: t('passwordChecks.hasUpper') },
                                      { met: pwdChecks.hasNumber, label: t('passwordChecks.hasNumber') },
                                      { met: pwdChecks.hasSpecial, label: t('passwordChecks.hasSpecial') },
                                      { met: pwdChecks.noTripleRepeat, label: t('passwordChecks.noTripleRepeat') },
                                    ].map(({ met, label }) => (
                                      <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {met
                                          ? <CheckCircleOutlineIcon sx={{ fontSize: 13, color: 'success.main' }} />
                                          : <RadioButtonUncheckedIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                                        }
                                        <Typography variant="caption" sx={{ color: met ? 'success.main' : 'text.disabled', lineHeight: 1.4 }}>
                                          {label}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                )}
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid container>
                          <Grid bgcolor="white" item xs>
                            <Grid container>
                              <Grid item xs={6} sx={{ py: '10px', pr: '8px' }}>
                                <Box
                                  display="flex"
                                  justifyContent="flex-start"
                                  marginBottom="4px"
                                >
                                  <InputLabel
                                    sx={{ color: 'black' }}
                                    htmlFor="provisional-password-confirm-input"
                                  >
                                    {t('fields.confirmProvisionalPassword')}
                                  </InputLabel>
                                </Box>
                                <TextField
                                  id="provisional-password-confirm-input"
                                  error={Boolean(
                                    formik.touched.provisional_password_confirm &&
                                      formik.errors.provisional_password_confirm,
                                  )}
                                  fullWidth
                                  helperText={
                                    formik.touched.provisional_password_confirm &&
                                    formik.errors.provisional_password_confirm
                                  }
                                  name="provisional_password_confirm"
                                  onBlur={formik.handleBlur}
                                  onChange={formik.handleChange}
                                  autoComplete="new-password"
                                  type={showConfirmPassword ? 'text' : 'password'}
                                  value={formik.values.provisional_password_confirm}
                                  variant="filled"
                                  placeholder={t('fields.confirmProvisionalPasswordPlaceholder')}
                                  InputProps={{
                                    disableUnderline: true,
                                    sx: {
                                      borderRadius: '6px',
                                      '.MuiInputBase-input': {
                                        paddingBottom: '16px',
                                        paddingTop: '16px',
                                      },
                                    },
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          onClick={() => setShowConfirmPassword(v => !v)}
                                          edge="end"
                                        >
                                          {showConfirmPassword ? (
                                            <VisibilityOff />
                                          ) : (
                                            <Visibility />
                                          )}
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                                {formik.values.provisional_password_confirm.length > 0 && (() => {
                                  const match =
                                    formik.values.provisional_password_confirm ===
                                    formik.values.provisional_password
                                  return (
                                    <Box sx={{ mt: 0.75, px: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      {match
                                        ? <CheckCircleOutlineIcon sx={{ fontSize: 13, color: 'success.main' }} />
                                        : <RadioButtonUncheckedIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                                      }
                                      <Typography variant="caption" sx={{ color: match ? 'success.main' : 'text.disabled', lineHeight: 1.4 }}>
                                        {t('passwordChecks.passwordsMatch')}
                                      </Typography>
                                    </Box>
                                  )
                                })()}
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                        {changeLink === 1 && (
                          <Grid container>
                            <Grid bgcolor="white" item xs>
                              <Grid container>
                                <Grid item xs={12} sx={{ py: '10px' }}>
                                  <Box
                                    display="flex"
                                    justifyContent="flex-start"
                                    marginBottom="4px"
                                  >
                                    <InputLabel
                                      sx={{ color: 'black' }}
                                      htmlFor="status-select"
                                    >
                                      {t('fields.assignToCompany')}
                                    </InputLabel>
                                  </Box>
                                  <EmpresasCheckboxList
                                    actualState={formik.values}
                                    onChange={formik.setValues}
                                  />
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ paddingBottom: '16px', paddingRight: '24px' }}>
              <Button color="primary" variant="outlined" onClick={handleClose}>
                {t('actions.cancel', { ns: 'common' })}
              </Button>
              <Button
                variant="contained"
                color="primary"
                form="crear-usuario-form"
                type="submit"
                disabled={
                  !formik.dirty ||
                  !formik.values.email ||
                  !formik.values.name ||
                  !formik.values.last_name ||
                  !formik.values.provisional_password ||
                  !formik.values.provisional_password_confirm ||
                  formik.values.companies.length <= 0 ||
                  disabledOnSubmit
                }
              >
                {t('createButton')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </div>
  )
}

export default NewUserModal
